import { useEffect, useState, type FormEvent } from 'react';
import type {
  ClientResponse,
  Invoice,
  InvoiceStatus,
  SubscriptionRead,
} from '@clarity/contract';
import {
  useClients,
  useSubscription,
  useSubscriptionInvoices,
  useSetOverride,
  useClearOverride,
  useToggleAddon,
  useSetAddonDiscount,
  useMarkInvoicePaid,
  useDownloadInvoicePdf,
} from '../queries/index.js';
import { QueryState } from './QueryState.js';
import { num, period, shortDate } from './format.js';
import { IconBilling } from '../components/icons.js';

/**
 * Admin-only subscription + invoice surface (clarity-subscriptions Req 15),
 * mounted at the top-level `/subscriptions` route behind the admin guard
 * (Req 15.2 — a non-admin is redirected to the non-admin fallback in
 * `AppRoutes`). Mirrors `Settings` / `IngestionRuns`: a `.page-head`, `.card`
 * surfaces, `QueryState` around the reads, controlled inputs, and the standard
 * `role="alert"` / `role="status"` state copy.
 *
 * The subscription view is PER-CLIENT (one subscription per tenant), so the
 * surface opens with a client selector — the admin picks a client, then the
 * subscription + invoice panels load for that client. All reads/writes go
 * through the typed TanStack Query hooks; the component never touches `fetch`.
 */
export function Subscription() {
  const clients = useClients();
  const clientRows: ClientResponse[] = clients.data?.status === 200 ? clients.data.body : [];
  const [clientId, setClientId] = useState('');

  const selected = clientRows.find((c) => c.id === clientId) ?? null;

  return (
    <section>
      <div className="page-head">
        <div>
          <h2>Billing</h2>
          <p className="sub">
            View and edit a client&apos;s subscription, and manage its invoices. All amounts are in
            JOD.
          </p>
        </div>
      </div>

      <QueryState isLoading={clients.isLoading} isError={clients.isError} label="clients">
        {clientRows.length === 0 ? (
          <div className="card empty">
            <IconBilling className="empty__glyph" />
            <p>No clients yet. Create a client first to manage its subscription.</p>
          </div>
        ) : (
          <div className="card sub-picker">
            <label htmlFor="sub-client">
              Client
              <select
                id="sub-client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              >
                <option value="">Select a client…</option>
                {clientRows.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="muted">Pick a client to view its subscription and invoices.</p>
          </div>
        )}
      </QueryState>

      {selected && <ClientBilling key={selected.id} client={selected} />}
    </section>
  );
}

/**
 * The subscription + invoice panels for one selected client. Keyed by client id
 * in the parent so switching clients remounts with fresh local state. The
 * per-client hooks are gated on a resolved id (always true here, but kept
 * explicit to mirror the other scoped reads).
 */
function ClientBilling({ client }: { client: ClientResponse }) {
  const subscription = useSubscription(client.id);
  const invoices = useSubscriptionInvoices(client.id);

  const read: SubscriptionRead | null =
    subscription.data?.status === 200 ? subscription.data.body : null;
  const invoiceRows: Invoice[] = invoices.data?.status === 200 ? invoices.data.body : [];

  return (
    <>
      <QueryState
        isLoading={subscription.isLoading}
        isError={subscription.isError}
        label="subscription"
      >
        {read ? (
          <SubscriptionCard clientId={client.id} read={read} />
        ) : (
          <div className="card empty">
            <IconBilling className="empty__glyph" />
            <p>
              <strong>{client.name}</strong> has no subscription yet.
            </p>
          </div>
        )}
      </QueryState>

      {read && (
        <QueryState isLoading={invoices.isLoading} isError={invoices.isError} label="invoices">
          <InvoiceList clientId={client.id} invoices={invoiceRows} />
        </QueryState>
      )}
    </>
  );
}

/** Locale-aware 2dp number string (JOD prices are always shown to 2dp, Req 15.1). */
function amount2dp(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** JOD label built on the fixed-2dp number so a 50 renders "50.00 JOD". */
function jodLabel(value: number): string {
  return `${amount2dp(value)} JOD`;
}

/**
 * The subscription view + edit controls (Req 15.1, 15.3, 15.4, 15.8).
 *
 * The view reads straight from the loaded `read` (enabled-store count, computed
 * base price, add-on state + amount, total monthly). Local input state (the
 * override field, the discount field) is RESEEDED from the loaded value via
 * `useEffect` — so when an edit route call errors, the invalidation never fires
 * and the displayed values stay at their last successfully loaded state
 * (Req 15.4). A successful edit invalidates the read, which refetches the
 * recomputed `SubscriptionRead`; the view then reflects the route's returned
 * value with no manual refetch (Req 15.3).
 */
function SubscriptionCard({ clientId, read }: { clientId: string; read: SubscriptionRead }) {
  const { subscription, pricing } = read;
  const suspended = subscription.status === 'suspended';

  const setOverride = useSetOverride(clientId);
  const clearOverride = useClearOverride(clientId);
  const toggleAddon = useToggleAddon(clientId);
  const setDiscount = useSetAddonDiscount(clientId);

  // The override input seeds from the stored override (blank when unset); the
  // discount input seeds from the stored discount (blank → the flat 100).
  const [override, setOverrideInput] = useState(
    subscription.priceOverride === undefined ? '' : String(subscription.priceOverride),
  );
  const [discount, setDiscountInput] = useState(
    subscription.addonDiscount === undefined ? '' : String(subscription.addonDiscount),
  );

  // Reseed local inputs from the loaded subscription whenever it changes (a
  // successful write refetches; an errored write does not, so the fields keep
  // the last-loaded values — Req 15.4).
  useEffect(() => {
    setOverrideInput(
      subscription.priceOverride === undefined ? '' : String(subscription.priceOverride),
    );
  }, [subscription.priceOverride]);
  useEffect(() => {
    setDiscountInput(
      subscription.addonDiscount === undefined ? '' : String(subscription.addonDiscount),
    );
  }, [subscription.addonDiscount]);

  function onSetOverride(e: FormEvent) {
    e.preventDefault();
    if (override.trim() === '') return;
    setOverride.mutate({ params: { clientId }, body: { priceOverride: Number(override) } });
  }

  function onClearOverride() {
    clearOverride.mutate({ params: { clientId } });
  }

  function onToggleAddon() {
    toggleAddon.mutate({ params: { clientId }, body: { enabled: !subscription.addonEnabled } });
  }

  function onSetDiscount(e: FormEvent) {
    e.preventDefault();
    if (discount.trim() === '') return;
    setDiscount.mutate({ params: { clientId }, body: { discount: Number(discount) } });
  }

  const anyError =
    setOverride.isError || clearOverride.isError || toggleAddon.isError || setDiscount.isError;

  return (
    <div className="card subscription-card">
      {suspended && (
        <div className="suspension-banner" role="alert">
          <IconBilling className="suspension-banner__glyph" aria-hidden="true" />
          <div>
            <strong>Subscription suspended</strong>
            <p>
              This client&apos;s stores are disabled for non-payment. Marking the overdue invoice
              paid re-enables them.
            </p>
          </div>
        </div>
      )}

      <h3>Subscription</h3>

      {/* The computed view (Req 15.1): enabled-store count, base, add-on, total. */}
      <dl className="subscription-stats">
        <div className="subscription-stat">
          <dt>Enabled stores</dt>
          <dd className="num">{num(pricing.enabledStoreCount)}</dd>
        </div>
        <div className="subscription-stat">
          <dt>Base price</dt>
          <dd className="num">{jodLabel(pricing.baseSubscriptionPrice)}</dd>
        </div>
        <div className="subscription-stat">
          <dt>Account-manager add-on</dt>
          <dd className="num">
            {subscription.addonEnabled ? jodLabel(pricing.addonAmount) : 'Off'}
          </dd>
        </div>
        <div className="subscription-stat subscription-stat--total">
          <dt>Total monthly</dt>
          <dd className="num">{jodLabel(pricing.totalMonthly)}</dd>
        </div>
      </dl>

      {/* ── Edit controls (Req 15.3) ── */}
      <div className="subscription-edit">
        <form className="subscription-field" onSubmit={onSetOverride} aria-label="Set price override">
          <label htmlFor="sub-override">
            Price override (JOD)
            <input
              id="sub-override"
              type="number"
              min={0}
              max={999999.99}
              step="0.01"
              value={override}
              onChange={(e) => setOverrideInput(e.target.value)}
              placeholder="e.g. 120.00"
            />
          </label>
          <div className="subscription-field__actions">
            <button type="submit" disabled={setOverride.isPending || override.trim() === ''}>
              {setOverride.isPending ? 'Saving…' : 'Set override'}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={onClearOverride}
              disabled={clearOverride.isPending || subscription.priceOverride === undefined}
            >
              {clearOverride.isPending ? 'Clearing…' : 'Clear override'}
            </button>
          </div>
          <p className="muted">
            {subscription.priceOverride === undefined
              ? 'No override — the base price follows the enabled-store ladder.'
              : `Override active: ${jodLabel(subscription.priceOverride)} replaces the ladder.`}
          </p>
        </form>

        <div className="subscription-field">
          <span className="subscription-field__label" id="sub-addon-label">
            Account-manager add-on
          </span>
          <div className="subscription-field__actions">
            <button
              type="button"
              className={subscription.addonEnabled ? 'ghost' : 'primary'}
              onClick={onToggleAddon}
              disabled={toggleAddon.isPending}
              aria-labelledby="sub-addon-label"
              aria-pressed={subscription.addonEnabled}
            >
              {toggleAddon.isPending
                ? 'Updating…'
                : subscription.addonEnabled
                  ? 'Disable add-on'
                  : 'Enable add-on'}
            </button>
          </div>

          {subscription.addonEnabled && (
            <form className="subscription-field__inline" onSubmit={onSetDiscount} aria-label="Set add-on discount">
              <label htmlFor="sub-discount">
                Add-on amount (JOD, 0–100)
                <input
                  id="sub-discount"
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={discount}
                  onChange={(e) => setDiscountInput(e.target.value)}
                  placeholder="100.00"
                />
              </label>
              <button type="submit" disabled={setDiscount.isPending || discount.trim() === ''}>
                {setDiscount.isPending ? 'Saving…' : 'Set amount'}
              </button>
            </form>
          )}
        </div>
      </div>

      {anyError && (
        <p role="alert" className="error">
          Could not update the subscription. The displayed values are unchanged.
        </p>
      )}
    </div>
  );
}

/** Human label per invoice status (Req 15.5 — text, never colour alone). */
const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  issued: 'Issued',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
};

/**
 * The invoice list (Req 15.5–15.7): one row per invoice with its period, total,
 * status, and due date, a mark-paid control for any non-`paid` invoice, and a
 * download-PDF control. An empty list gets its own clear state.
 */
function InvoiceList({ clientId, invoices }: { clientId: string; invoices: Invoice[] }) {
  if (invoices.length === 0) {
    return (
      <div className="card empty">
        <IconBilling className="empty__glyph" />
        <p>No invoices yet. They are generated automatically at each billing cycle.</p>
      </div>
    );
  }

  return (
    <div className="card invoice-table__wrap">
      <h3>Invoices</h3>
      <table className="data-table invoice-table">
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">Total</th>
            <th scope="col">Status</th>
            <th scope="col">Due</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <InvoiceRow key={invoice.id} clientId={clientId} invoice={invoice} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One invoice row. Mark-paid flips the row to `paid` on success (the hook
 * invalidates the list, which refetches — Req 15.6) and surfaces the error with
 * the status left unchanged on failure (Req 15.7). The paid amount submitted is
 * the invoice total due (the server validates `amount === total`, Req 9.4).
 * Download fetches the scoped PDF bytes and triggers a browser download; a `409`
 * (`not_stored`) surfaces a non-blocking "not available yet" message (Req 15.6).
 */
function InvoiceRow({ clientId, invoice }: { clientId: string; invoice: Invoice }) {
  const markPaid = useMarkInvoicePaid(clientId);
  const downloadPdf = useDownloadInvoicePdf();

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const isPaid = invoice.status === 'paid';

  function onMarkPaid() {
    markPaid.mutate({ params: { invoiceId: invoice.id }, body: { amount: invoice.total } });
  }

  async function onDownload() {
    setDownloadError(null);
    setDownloading(true);
    try {
      const res = await downloadPdf({ params: { invoiceId: invoice.id } });
      if (res.status === 200) {
        // ts-rest types the PDF body as `Uint8Array` over an `ArrayBufferLike`
        // (the dependency-free contract leaf); copy into a plain `Uint8Array`
        // so the `Blob` part type narrows away the `SharedArrayBuffer` union.
        const bytes = new Uint8Array(res.body);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `invoice-${invoice.id}.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      } else {
        // 409 (not_stored) or any other non-200 → a non-blocking notice.
        setDownloadError('The PDF is not available yet.');
      }
    } catch {
      setDownloadError('Could not download the PDF.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <tr className={`invoice-row invoice-row--${invoice.status}`}>
      <td className="invoice-row__period">{period(invoice.periodStart, invoice.periodEnd)}</td>
      <td className="invoice-row__total num">{jodLabel(invoice.total)}</td>
      <td className="invoice-row__status">
        <span className={`badge invoice-badge invoice-badge--${invoice.status}`}>
          {INVOICE_STATUS_LABEL[invoice.status]}
        </span>
      </td>
      <td className="invoice-row__due num">{shortDate(invoice.dueDate)}</td>
      <td className="invoice-row__actions">
        <div className="invoice-row__controls">
          {!isPaid && (
            <button
              type="button"
              className="sm"
              onClick={onMarkPaid}
              disabled={markPaid.isPending}
            >
              {markPaid.isPending ? 'Marking…' : 'Mark paid'}
            </button>
          )}
          <button
            type="button"
            className="sm ghost"
            onClick={onDownload}
            disabled={downloading}
            aria-label={`Download the PDF for the invoice covering ${period(invoice.periodStart, invoice.periodEnd)}`}
          >
            {downloading ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>
        {markPaid.isError && (
          <p role="alert" className="error invoice-row__msg">
            Could not mark this invoice paid. Its status is unchanged.
          </p>
        )}
        {downloadError && (
          <p role="status" className="muted invoice-row__msg">
            {downloadError}
          </p>
        )}
      </td>
    </tr>
  );
}
